import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, SvgIcon } from '@mui/material';
import { keyframes } from '@emotion/react';
import { COLORS, FONTS } from '../theme';

/* ------------------------------ keyframes ------------------------------ */
export const float = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-18px)}`;
export const floatSlow = keyframes`0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-26px) rotate(8deg)}`;
export const wiggle = keyframes`0%,100%{transform:rotate(-8deg)}50%{transform:rotate(8deg)}`;
export const spin = keyframes`to{transform:rotate(360deg)}`;
export const drift = keyframes`0%{transform:translateX(-30vw)}100%{transform:translateX(130vw)}`;
export const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.07)}`;
export const ring = keyframes`0%{box-shadow:0 0 0 0 rgba(55,178,77,.55)}100%{box-shadow:0 0 0 22px rgba(55,178,77,0)}`;
export const marquee = keyframes`0%{transform:translateX(0)}100%{transform:translateX(-50%)}`;
export const morph = keyframes`0%,100%{border-radius:60% 40% 55% 45% / 50% 60% 40% 50%}50%{border-radius:40% 60% 45% 55% / 60% 40% 60% 40%}`;
export const popIn = keyframes`0%{transform:scale(0.6);opacity:0}70%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}`;

/* ------------------------------- hooks -------------------------------- */
export function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setY(window.scrollY || 0));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return y;
}

export function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ------------------------------- Reveal -------------------------------- */
const FROM = {
  up: 'translateY(60px)',
  down: 'translateY(-60px)',
  left: 'translateX(-70px)',
  right: 'translateX(70px)',
  zoom: 'scale(0.7)',
  rotate: 'rotate(-6deg) translateY(40px)',
};

export function Reveal({ children, direction = 'up', delay = 0, duration = 800, sx }) {
  const [ref, inView] = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : FROM[direction],
        transition: `opacity ${duration}ms ease ${delay}ms, transform ${duration}ms cubic-bezier(.2,.9,.3,1.15) ${delay}ms`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', opacity: 1, transform: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/* ------------------------------- CountUp ------------------------------- */
export function CountUp({ end, suffix = '', duration = 1600 }) {
  const [ref, inView] = useInView(0.4);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

/* -------------------------------- Icons -------------------------------- */
const PATHS = {
  phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  pin: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  clock: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
  star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  menu: 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z',
  close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
  shield: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
  heart: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
  meal: 'M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z',
  palette: 'M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  group: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  book: 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z',
  bulb: 'M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z',
  mail: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  arrow: 'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z',
  sun: 'M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zm7.45 3.91l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79zm-3.21 13.7l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-3.91l1.79 1.8 1.41-1.41-1.79-1.8-1.41 1.41z',
  moon: 'M10 2c-1.82 0-3.53.5-5 1.35C7.99 5.08 10 8.3 10 12s-2.01 6.92-5 8.65C6.47 21.5 8.18 22 10 22c5.52 0 10-4.48 10-10S15.52 2 10 2z',
  image: 'M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z',
  up: 'M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z',
  plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
};

export function Icon({ name, sx, ...rest }) {
  return (
    <SvgIcon sx={sx} {...rest}>
      <path d={PATHS[name] || PATHS.star} />
    </SvgIcon>
  );
}

/* ------------------------------- ImageBox ------------------------------ */
export function ImageBox({ src, label, color = COLORS.blue, ratio = '4 / 3', radius = 6, sx }) {
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={label}
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: ratio || undefined,
          height: ratio ? undefined : '100%',
          objectFit: 'cover',
          borderRadius: radius,
          ...sx,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: ratio || undefined,
        height: ratio ? undefined : '100%',
        borderRadius: radius,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${color}40 0%, ${color}99 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        color: '#fff',
        textAlign: 'center',
        p: 2,
        ...sx,
      }}
    >
      <Box sx={{ position: 'absolute', top: '-12%', right: '-8%', width: '45%', aspectRatio: '1', borderRadius: '50%', bgcolor: 'rgba(255,255,255,.28)' }} />
      <Box sx={{ position: 'absolute', bottom: '-14%', left: '-10%', width: '55%', aspectRatio: '1', borderRadius: '50%', bgcolor: 'rgba(255,255,255,.2)' }} />
      <Icon name="image" sx={{ fontSize: 54, position: 'relative', filter: 'drop-shadow(0 3px 4px rgba(0,0,0,.2))' }} />
      <Typography sx={{ position: 'relative', fontFamily: FONTS.heading, fontWeight: 700, fontSize: 16, textShadow: '0 2px 4px rgba(0,0,0,.25)' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ------------------------------ Section bits --------------------------- */
export function SectionHeading({ eyebrow, title, subtitle, color = COLORS.pink, light = false, align = 'center' }) {
  return (
    <Reveal sx={{ textAlign: align, maxWidth: 780, mx: align === 'center' ? 'auto' : 0, mb: { xs: 5, md: 7 } }}>
      <Box
        sx={{
          display: 'inline-block',
          px: 2.5,
          py: 0.6,
          mb: 1.5,
          borderRadius: 50,
          bgcolor: light ? 'rgba(255,255,255,.2)' : `${color}22`,
          color: light ? '#fff' : color,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Box>
      <Typography variant="h2" sx={{ color: light ? '#fff' : COLORS.navy, fontSize: { xs: 34, md: 50 }, lineHeight: 1.1 }}>
        {title}
      </Typography>
      <Box
        component="svg"
        viewBox="0 0 120 12"
        sx={{ display: 'block', width: 120, height: 12, mt: 1.5, mx: align === 'center' ? 'auto' : 0 }}
      >
        <path d="M2 6 Q 17 -2 32 6 T 62 6 T 92 6 T 118 6" stroke={light ? COLORS.yellow : color} strokeWidth="4" fill="none" strokeLinecap="round" />
      </Box>
      {subtitle && (
        <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 18 }, lineHeight: 1.7, color: light ? 'rgba(255,255,255,.9)' : COLORS.text }}>
          {subtitle}
        </Typography>
      )}
    </Reveal>
  );
}

export function Wave({ color, flip = false, sx }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      sx={{ display: 'block', width: '100%', height: { xs: 50, md: 90 }, transform: flip ? 'rotate(180deg)' : 'none', ...sx }}
    >
      <path fill={color} d="M0,64 C240,120 480,0 720,48 C960,96 1200,16 1440,64 L1440,120 L0,120 Z" />
    </Box>
  );
}

export function Section({ id, bg, children, sx }) {
  return (
    <Box component="section" id={id} sx={{ position: 'relative', scrollMarginTop: '70px', bgcolor: bg, overflow: 'hidden', ...sx }}>
      {children}
    </Box>
  );
}
